import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import './EventsSection.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import TicketModal from './TicketModal';

/**
 * EventsSection Component
 * Displays a list of upcoming events/bookings fetched from the backend.
 * Can filter events based on a selected category.
 */
function EventsSection({ selectedCategory }) {
  // Local state for fetched events and loading status
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicketEvent, setSelectedTicketEvent] = useState(null);

  // Fetch public events from the backend database when component mounts
  useEffect(() => {
    async function fetchPublicEvents() {
      try {
        const { data, error } = await supabase
          .from('tbl_events')
          .select('*')
          .order('event_date', { ascending: true });

        if (error) throw error;
        setEvents(data || []);
      } catch (error) {
        console.error("Failed to fetch public events:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchPublicEvents();
  }, []);

  const categoryEvents = category => events.filter(event => event.category === category);
  const selectedEvents = selectedCategory ? categoryEvents(selectedCategory) : [];

  const todayEvents = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return events.filter(e => {
      if (!e.event_date && !e.date) return false;
      const eDate = new Date(e.event_date || e.date).toISOString().split('T')[0];
      return eDate === today;
    });
  }, [events]);

  // Helper to parse dates into beautiful calendar format
  const parseDate = (dateString) => {
    if (!dateString) return { day: '', month: '', full: 'TBD' };
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return { day: '', month: '', full: dateString };
      return {
        day: d.toLocaleDateString('en-GB', { day: '2-digit' }),
        month: d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
        full: d.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
      };
    } catch {
      return { day: '', month: '', full: dateString };
    }
  };

  if (loading) {
    return (
      <section id="bookings" className="booking-section">
        <div className="booking-panel">
          <div className="booking-empty">Loading upcoming events...</div>
        </div>
      </section>
    );
  }

  // Component to render an individual premium event card
  const EventCard = ({ event }) => {
    const dateObj = parseDate(event.event_date || event.date);
    const categoryClass = `badge-${event.category?.toLowerCase() || 'activities'}`;
    const categoryName = event.category === 'community' ? 'Community' : event.category === 'sports' ? 'Sports' : 'Activities';

    return (
      <article className="event-card-premium">
        {event.poster_url ? (
          <img src={event.poster_url} alt={event.title} className="event-poster" />
        ) : (
          <div className="event-poster-placeholder">
            {event.title}
          </div>
        )}
        <div className="event-calendar-box">
          {dateObj.day ? (
            <>
              <span className="event-cal-day">{dateObj.day}</span>
              <span className="event-cal-month">{dateObj.month}</span>
            </>
          ) : (
            <span className="event-cal-fallback">{dateObj.full}</span>
          )}
        </div>
        <div className="event-body">
          <div className="event-title-row">
            <h3>{event.title}</h3>
            <span className={`event-badge-premium ${categoryClass}`}>
              {categoryName}
            </span>
          </div>
          <div className="event-meta">
            {event.location && (
              <span className="event-meta-item">
                <i className="icon-location" /> {event.location}
              </span>
            )}
            {(event.start_time || event.end_time) && (
              <span className="event-meta-item">
                <i className="icon-clock" /> {event.start_time || ''} {event.end_time ? `- ${event.end_time}` : ''}
              </span>
            )}
          </div>
          <p className="event-description">{event.description}</p>
          <button className="btn-ticket" onClick={() => setSelectedTicketEvent(event)}>
            Book Session
          </button>
        </div>
      </article>
    );
  };

  return (
    <section id="bookings" className="booking-section">
      <div className="booking-panel">
        <div className="booking-header">
          <div>
            <h2>Bookings & Upcoming Events</h2>
            <p>Browse sessions, events and activities available for booking. Select any category on the home page and press View to see related event details.</p>
          </div>
          <div className="booking-count">
            {events.length > 0 ? `${events.length} upcoming event${events.length > 1 ? 's' : ''}` : 'No events scheduled'}
          </div>
        </div>

        {todayEvents.length > 0 && (
          <div className="today-section">
            <h3 className="today-header">What's on today</h3>
            <div className="today-event-list">
              {todayEvents.map(event => (
                <EventCard key={`today-${event.id}`} event={event} />
              ))}
            </div>
          </div>
        )}

        {events.length > 0 ? (
          <div className="events-list">
            {events.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="booking-empty">No upcoming events are currently scheduled.</div>
        )}

        {selectedCategory && (
          <div className="events-list" style={{ borderTop: '2px solid #f4f7f5', marginTop: '20px' }}>
            <h3 style={{ padding: '20px 40px 0', margin: 0, color: '#1e3f32', fontFamily: '"Merriweather", serif' }}>
              Filtered: {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
            </h3>
            {selectedEvents.length > 0 ? (
              selectedEvents.map(event => (
                <EventCard key={event.id} event={event} />
              ))
            ) : (
              <div className="booking-empty" style={{ padding: '30px' }}>No events found for this specific category.</div>
            )}
          </div>
        )}
        <TicketModal event={selectedTicketEvent} onClose={() => setSelectedTicketEvent(null)} />
      </div>
    </section>
  );
}

export default EventsSection;